<template>
  <div class="content-wrapper step-1">
    <div class="step-wrapper">
      <div class="step">Etape 1/3</div>
      <div class="header-content">
        <h1>Créez votre campagne</h1>
        <p>
          Les informations entrées ci-dessous sont dédiées à votre organisation
          interne. Elles ne seront pas visibles du talent.
        </p>
        <div class="text-wrapper">
          <div class="text-with-icon">
            Le saviez-vous ? Ce symbole <i class="yuzu-icon yuzu-ai" />
          </div>
          apparait lorsque que l’Intelligence Artificielle Yuzu AI est utilisée.
        </div>
      </div>
      <div class="form-wrapper">
        <VField
          label="Nom de votre campagne"
          placeholder="Saisissez le nom de votre campagne"
          name="name"
          v-model="state.campaign_name"
          :required="true"
        />
        <div class="input-wrapper">
          <label>
            Espace de votre campagne
            <span class="primary-color">*</span>
          </label>
          <v-select
            id="campaign-space"
            placeholder="Choisissez l’espace dans lequel sera rangée votre campagne"
            name="campaign-space"
            label="name"
            v-model="state.workspace_id"
            :reduce="(space) => space.workspace_id"
            :clearable="false"
            :options="spaceStore.companySpaces"
          >
            <template #list-footer>
              <li
                id="create-space"
                class="vs__dropdown-option"
                role="option"
                @click="state.spaceModalIsOpen = true"
              >
                <i class="bi bi-plus-lg" />
                Créer un nouvel espace
              </li>
            </template>
            <template v-slot:no-options="{ search, searching }">
              <div style="padding: 8px 0">
                Pas de résultat {{ searching ? ` pour ${search}` : "" }}.
              </div>
            </template>
          </v-select>
        </div>
        <div class="input-wrapper">
          <VField
            label="Métier évalué par votre campagne"
            placeholder="Sélectionnez le métier qui sera évalué par votre campagne"
            name="campaign-job"
            v-model="state.job"
            iconL="yuzu-icon yuzu-ai"
          />
        </div>
        <div class="checkbox-wrapper">
          <input type="checkbox"
            name="nojob"
            id="nojob"
            v-model="state.nojob"
          />
          <label for="nojob">Ma campagne ne vise pas à évaluer un métier en particulier</label> 
        </div>
      </div>
      <Button
        class="icon primary no-dark-mode"
        :disabled="!canGoNext"
        :label="$t('campaign.newPage.createCampaign')"
        icon="bi-chevron-right"
        @click="
          emits('next', {
            name: state.campaign_name,
            workspace_id: state.workspace_id,
            job: state.job,
            nojob: state.nojob,
          })
        "
        v-tooltip="{
          content: 'Tous les champs ne sont pas remplis.',
          triggers: canGoNext ? [] : ['hover'],
        }"
      />
    </div>
    <SpaceModal
      :isOpen="state.spaceModalIsOpen"
      :space="state.space"
      action="create"
      @close="(state.spaceModalIsOpen = false), (state.space = null)"
    />
  </div>
</template>

<style scoped lang="scss">
.checkbox-wrapper {
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 8px;
}
</style>

<script setup>
import { reactive, computed, onMounted } from "vue";
import { useSpaceStore } from "@/stores/space.js";
import { useRoute, onBeforeRouteLeave } from "vue-router";
import { useAppStore } from "@/stores/app.js";
import SpaceModal from "@/components/modals/app/SpaceModal.vue";

const emits = defineEmits(["next"]);

const appStore = useAppStore();

const route = useRoute();

const spaceStore = useSpaceStore();

const props = defineProps({
  campaignData: {
    type: Object,
  },
});

const state = reactive({
  campaign_name: props.campaignData.name,
  workspace_id: props.campaignData.workspace_id,
  spaceModalIsOpen: false,
  job: props.campaignData.job,
  nojob: props.campaignData.nojob,
});

if (!spaceStore.companySpaces.length > 0) {
  await spaceStore.getSpaces(route.params.companyId);
}

const canGoNext = computed(() => {
  return (
    state.campaign_name &&
    state.campaign_name.length <= 50 &&
    state.workspace_id
  );
});

if (!appStore.isOnboarded) {
  onBeforeRouteLeave((to, from, next) => {
    if (appStore.chapterState.isComplete) {
      window.confirm("Souhaitez-vous vraiment quitter l'onboarding ?")
        ? (next(), (appStore.isOnboarded = true))
        : false;
    }
  });
}

onMounted(async () => {
  if (route.query.spaceId) {
    state.workspace_id = route.query.spaceId;
  }

  if (!appStore.isOnboarded) {
    const newCampaignModule = await import("@/onboarding/newCampaign.js");

    setTimeout(() => {
      appStore.startChapter(newCampaignModule.default);
    }, 300);
  }
});
</script>
